import React, { useEffect, useRef } from 'react';
import { AddressAutofill } from '@mapbox/search-js-react';

/* How long after a suggestion is picked we still treat input/blur events as
   "caused by the selection" rather than by the user typing. */
const SELECTION_GRACE_MS = 1000;
const BLUR_CHECK_DELAY_MS = 200;

const AU_STATES = [
  'new south wales',
  'nsw',
  'victoria',
  'vic',
  'queensland',
  'qld',
  'south australia',
  'sa',
  'western australia',
  'wa',
  'tasmania',
  'tas',
  'northern territory',
  'nt',
  'australian capital territory',
  'act',
];

/* Builds the same address object from a full address string.
   Used as a safety net when the Mapbox retrieve event doesn't fire or arrives
   in a different shape than expected — a real pick must never be rejected. */
const parseFullAddress = (text) => {
  const value = String(text || '').trim();
  const out = {
    full: value,
    street: '',
    route: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  };

  const parts = value
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return out;

  // the segment holding the postcode (or the last non-country one)
  let tailIndex = -1;
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    if (/\b\d{3,5}\b/.test(parts[i])) {
      tailIndex = i;
      break;
    }
  }
  if (tailIndex === -1) {
    tailIndex = parts.length - 1;
    if (parts.length > 1 && !/\d/.test(parts[parts.length - 1])) {
      tailIndex = parts.length - 2;
    }
  }

  const tailTokens = parts[tailIndex].split(/\s+/);
  if (/^\d{3,5}$/.test(tailTokens[tailTokens.length - 1] || '')) {
    out.zip = tailTokens.pop();
  }
  for (let len = 3; len >= 1; len -= 1) {
    if (tailTokens.length <= len) continue; // keep at least the suburb
    if (AU_STATES.includes(tailTokens.slice(-len).join(' ').toLowerCase())) {
      out.state = tailTokens.slice(-len).join(' ');
      tailTokens.splice(-len, len);
      break;
    }
  }
  out.city = tailTokens.join(' ');

  const countryIndex =
    tailIndex < parts.length - 1 && !/\d/.test(parts[parts.length - 1])
      ? parts.length - 1
      : -1;
  if (countryIndex !== -1) out.country = parts[countryIndex];

  out.street = parts
    .filter((_, i) => i !== tailIndex && i !== countryIndex)
    .join(', ');

  return out;
};

/* "1 Bligh Street, Sydney NSW 2000" → true; "1 Bl" → false */
const looksLikeFullAddress = (text) => {
  const parsed = parseFullAddress(text);
  return Boolean(
    parsed.street && parsed.city && /\d{3,5}/.test(parsed.zip || ''),
  );
};

const AddressAutocomplete = ({
  onSelect,
  value = '',
  placeholder = 'Enter address',
  showToast,
  className = 'w-full',
  disabled,
}) => {
  const inputRef = useRef(null);

  const placeSelectedRef = useRef(false);
  const lastRetrieveAtRef = useRef(0);
  const lastCommittedValueRef = useRef('');
  const typedLengthRef = useRef(0);

  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

  /* Accept every payload shape Mapbox uses:
     React  → event.features[0]              (response.features)
     Web    → event.detail.features[0]
     Custom → event.detail[0] / event.feature */
  const extractFeature = (event) =>
    event?.features?.[0] ||
    event?.detail?.features?.[0] ||
    (Array.isArray(event?.detail) ? event.detail[0] : null) ||
    event?.feature ||
    null;

  /**
   * Mapbox returns the selected address through the autofill event.
   */
  const handleRetrieve = (event) => {
    const feature = extractFeature(event);
    console.log('feature is ', feature);

    if (!feature) {
      console.warn(
        '[AddressAutocomplete] retrieve fired with an unexpected payload:',
        event,
      );
      return;
    }

    // Suggestions carry the fields flat; retrieved features nest them
    const properties = feature.properties || feature || {};
    const context = properties.context || {};

    const address =
      properties.full_address || properties.name || feature.place_name || '';

    const addressData = {
      full: address,
      street: '',
      route: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    };

    /**
     * Mapbox address information can be represented
     * through feature properties/context.
     */

    // Street number
    if (properties.address_line1) {
      addressData.street = properties.address_line1;
    }

    // Street / road
    if (properties.street) {
      addressData.route = properties.street;
    }

    // City
    addressData.city =
      properties.place || context.locality || context.district || '';

    // State
    addressData.state = properties.region || properties.address_level1 || '';

    // Postcode
    addressData.zip = properties.postcode || '';

    // Country
    addressData.country = properties.country || '';

    // Mark the pick FIRST: clicking a suggestion blurs the input, and the
    // blur/input handlers below can fire before (or right after) this callback.
    placeSelectedRef.current = true;
    lastRetrieveAtRef.current = Date.now();

    // Update input immediately
    if (inputRef.current) {
      inputRef.current.value = addressData.full;
      typedLengthRef.current = addressData.full.length;
    }

    lastCommittedValueRef.current = addressData.full;
    console.log('adddres is ', addressData);
    onSelect?.(addressData);
  };

  /**
   * Keep external value in sync with the input.
   */
  useEffect(() => {
    if (!inputRef.current) return;

    if (
      value &&
      value !== lastCommittedValueRef.current &&
      value !== inputRef.current.value
    ) {
      inputRef.current.value = value;
      lastCommittedValueRef.current = value;
      typedLengthRef.current = value.length;
    }

    if (!value && inputRef.current.value) {
      inputRef.current.value = '';
      lastCommittedValueRef.current = '';
      typedLengthRef.current = 0;
    }
  }, [value]);

  /**
   * User starts typing.
   *
   * Two things to ignore here:
   *  - input events fired by the autofill right after a retrieve
   *  - a value that jumps in by more than one character (that's the autofill
   *    writing the whole address, not the user typing it)
   */
  const handleChange = (event) => {
    const next = event?.target?.value ?? '';

    if (Date.now() - lastRetrieveAtRef.current < SELECTION_GRACE_MS) return;

    const jumped = next.length - typedLengthRef.current > 1;
    typedLengthRef.current = next.length;

    if (jumped && looksLikeFullAddress(next)) {
      placeSelectedRef.current = true;
      lastCommittedValueRef.current = next;
      onSelect?.(parseFullAddress(next));
      return;
    }

    placeSelectedRef.current = false;
  };

  /**
   * Validate that the user selected an address
   * from the Mapbox suggestions.
   *
   * Clicking a suggestion blurs the input BEFORE onRetrieve runs, so the check
   * is deferred, and a value that already looks like a full address is
   * accepted even if the retrieve event is missing or late.
   */
  const handleBlur = () => {
    setTimeout(() => {
      const currentValue = (inputRef.current?.value ?? '').trim();

      if (!currentValue) {
        onSelect?.(null);
        lastCommittedValueRef.current = '';
        typedLengthRef.current = 0;
        placeSelectedRef.current = false;
        return;
      }

      const justPicked =
        placeSelectedRef.current ||
        Date.now() - lastRetrieveAtRef.current < SELECTION_GRACE_MS;

      if (justPicked || currentValue === lastCommittedValueRef.current) {
        placeSelectedRef.current = false;
        lastCommittedValueRef.current = currentValue;
        if (!justPicked) onSelect?.(parseFullAddress(currentValue));
        return;
      }

      // Last safety net: it already reads like a complete address
      if (looksLikeFullAddress(currentValue)) {
        lastCommittedValueRef.current = currentValue;
        onSelect?.(parseFullAddress(currentValue));
        return;
      }

      showToast?.('Please select an address from the suggestions.', 'error');

      if (inputRef.current) {
        inputRef.current.value = '';
      }

      lastCommittedValueRef.current = '';
      typedLengthRef.current = 0;
      placeSelectedRef.current = false;
      onSelect?.(null);
    }, BLUR_CHECK_DELAY_MS);
  };

  if (!accessToken) {
    console.error(
      'Mapbox access token is missing. Please configure REACT_APP_MAPBOX_ACCESS_TOKEN.',
    );
  }

  return (
    <AddressAutofill
      accessToken={accessToken}
      onRetrieve={handleRetrieve}
      options={{
        country: 'AU',
      }}
    >
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        autoComplete="address-line1"
        className={`border p-2 rounded-md shadow-sm transition-all ${className}`}
      />
    </AddressAutofill>
  );
};

export default AddressAutocomplete;
